import { useContext, useMemo } from "react"
import AnalysisContext from "../../context/Analysis"
import PitchContext from "../../context/Pitch"
import { addHours } from "date-fns"
import useTempestHistory from "../../hooks/useTempestHistory"
import { css } from "@emotion/react"
import { colors } from "shared-ui"
import WeatherFieldCard from "../weather/WeatherFieldCard"
import tempHighIcon from '../../assets/icons/weather/temperatureHigh.svg'
import tempLowIcon from '../../assets/icons/weather/temperatureLow.svg'
import rainIcon from '../../assets/icons/weather/rain.svg'
import windIcon from '../../assets/icons/weather/wind.svg'
import { ReactSVG } from "react-svg"
import stationIcon from '../../assets/icons/weather/station.svg'
import { FormattedMessage, useIntl } from "react-intl"
import AnalysisPdfData from "../../context/AnalysisDetailsPdfData"
import { useEffect } from "react"

export default function AnalysisWeather({ ...props }) {

    const { formatMessage } = useIntl()
    const { data: analysis, isBusy: analysisWaiting } = useContext(AnalysisContext)
    const { data: pitch, isBusy: pitchBusy } = useContext(PitchContext)

    const { setTempestWeather } = useContext(AnalysisPdfData)

    const analysisDate = useMemo(() => {
        if (analysis?.date) return new Date(Number(analysis.date) * 1000)
    }, [analysis])


    //TEMPEST WEATHERSTATION
    const stationId = useMemo(() => {
        if (pitch?.weatherFlowStations.length > 0) return pitch.weatherFlowStations[0].stationId
    }, [pitch])

    const { allObservations, isBusy } = useTempestHistory(
        analysisDate ? stationId : null,
        analysisDate ? Math.floor(analysisDate / 1000) : null,
        analysisDate ? Math.floor(addHours(analysisDate, 1) / 1000) : null,
        'c', // 30 min bucket
    )

    const isLoading = useMemo(() => {
        return pitchBusy || analysisWaiting || isBusy
    }, [pitchBusy, analysisWaiting, isBusy])

    const { tempTempest, windAvgTempest, dailyRainTotalTempest, humidityAvgTempest, hasTempestData } = useMemo(() => {
        if (!allObservations || allObservations.length === 0) return { hasTempestData: false }

        const firstObs = allObservations[0].obs[0]
        return {
            tempTempest: firstObs[7],
            windAvgTempest: firstObs[2],
            dailyRainTotalTempest: Math.round(firstObs[18] * 100) / 100,
            humidityAvgTempest: firstObs[8],
            hasTempestData: true,
        }
    }, [allObservations])

    useEffect(() => {
        if (!hasTempestData) return
        setTempestWeather(
            {
                tempTempest,
                windAvgTempest,
                dailyRainTotalTempest,
                humidityAvgTempest,
                bulbGlobeTemperature: calcBulbGlobeTemperatureCelcius(tempTempest, humidityAvgTempest)
            }
        )
    }, [hasTempestData])

    return (hasTempestData) && <div div css={css` 
            width: 100%; 
            margin-bottom: 1em;
            border-radius: 5px;
            display: flex;
            justify-content: space-between;
            gap: 0.2em;
            flex-wrap: wrap;
        `}
        id='analysis-weather-summary'
    >
        {hasTempestData && <div css={css`
                border-radius: 5px;
                background: ${colors.eventLight};
                display: grid;
                place-items: center;
                padding: 1em;
                color: ${colors.soft};
            `}>
            <ReactSVG
                src={stationIcon}
                css={css`
                        width: 1.8em;
                        height: 1.8em;
                    `}
            />
            <FormattedMessage id='station' />
        </div>}

        <WeatherFieldCard
            field={'x'}
            value={tempTempest}
            unit={'°C'}
            label={formatMessage({ id: 'temperature' })}
            icon={tempHighIcon}
        />
        <WeatherFieldCard
            field={'x'}
            value={dailyRainTotalTempest}
            unit={'mm'}
            label={formatMessage({ id: 'precipitation' })}
            icon={rainIcon}
        />
        <WeatherFieldCard
            field={'x'}
            value={windAvgTempest}
            unit={'m/s'}
            label={`${formatMessage({ id: 'wind' })} ${formatMessage({ id: 'averageAbbr' })}`}
            icon={windIcon}
        />
        <WeatherFieldCard
            field={'x'}
            value={calcBulbGlobeTemperatureCelcius(
                tempTempest,
                humidityAvgTempest,
            )}
            unit={'°C'}
            label={formatMessage({ id: 'wetBulbTemperature' })}
            icon={tempLowIcon}
        />
    </div>
}

function calcBulbGlobeTemperatureCelcius(tempCelcius, humidityPerc) {
    // Stull's formula
    const wetBulb = (
        tempCelcius * Math.atan(0.151977 * Math.sqrt(humidityPerc + 8.313659))
        + Math.atan(tempCelcius + humidityPerc)
        - Math.atan(humidityPerc - 1.676331)
        + 0.00391838 * (humidityPerc) ** (3 / 2) * Math.atan(0.023101 * humidityPerc)
        - 4.686035
    )

    return Math.round(wetBulb * 10) / 10
}